import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import DOMPurify from "dompurify";
import { Navbar } from "@/components/blocks/Navbar";
import { Footer } from "@/components/blocks/footer";
import { Seo } from "@/components/Seo";
import { supabase } from "@/integrations/supabase/client";

interface FaqEntry {
  question: string;
  answer: string;
}

interface BlogPostRecord {
  id: string;
  title: string;
  slug: string;
  meta_description: string | null;
  content_html: string | null;
  hero_image_url: string | null;
  hero_image_alt: string | null;
  infographic_image_url: string | null;
  faq_schema: FaqEntry[] | null;
  language_code: string;
  published_at: string;
  updated_at: string;
}

const footerData = {
  logo: <img src="/lovable-uploads/c22d7cd3-d7b2-4b69-9087-2e231ebb8feb.png" alt="Quickatoo Logo" className="h-10 w-auto" />,
  brandName: "Quickatoo",
  socialLinks: [],
  mainLinks: [{ href: "/", label: "Home" }, { href: "/blog", label: "Blog" }],
  legalLinks: [{ href: "/privacy", label: "Privacy" }, { href: "/terms", label: "Terms" }],
  copyright: { text: "© 2024 Quickatoo", license: "All rights reserved" },
};

const formatDate = (value: string) =>
  new Date(value).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" });

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<BlogPostRecord | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!slug) return;
    setLoading(true);
    (async () => {
      const { data, error } = await supabase
        .from("blog_posts")
        .select(
          "id, title, slug, meta_description, content_html, hero_image_url, hero_image_alt, infographic_image_url, faq_schema, language_code, published_at, updated_at",
        )
        .eq("slug", slug)
        .eq("status", "published")
        .maybeSingle();

      if (error) console.error("Failed to load blog post:", error.message);
      setPost((data as unknown as BlogPostRecord) ?? null);
      setLoading(false);
    })();
  }, [slug]);

  if (loading) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen bg-background pt-32 pb-20">
          <p className="text-center text-muted-foreground">Loading article…</p>
        </main>
      </>
    );
  }

  if (!post) {
    return (
      <>
        <Seo title="Article not found — Quickatoo" description="This article could not be found." path={`/blog/${slug ?? ""}`} noindex />
        <Navbar />
        <main className="min-h-screen bg-background pt-32 pb-20 text-center">
          <h1 className="text-3xl font-bold text-foreground">Article not found</h1>
          <p className="mt-4 text-muted-foreground">The article you're looking for doesn't exist or was removed.</p>
          <Link to="/blog" className="mt-6 inline-block font-medium text-primary">
            ← Back to the blog
          </Link>
        </main>
        <Footer {...footerData} />
      </>
    );
  }

  const faqs = Array.isArray(post.faq_schema) ? post.faq_schema : [];

  const jsonLd: Record<string, unknown>[] = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: post.title,
      description: post.meta_description ?? undefined,
      image: post.hero_image_url ?? undefined,
      inLanguage: post.language_code,
      datePublished: post.published_at,
      dateModified: post.updated_at,
      mainEntityOfPage: `https://quickatoo.com/blog/${post.slug}`,
      author: { "@type": "Organization", name: "Quickatoo" },
      publisher: { "@type": "Organization", name: "Quickatoo" },
    },
  ];

  if (faqs.length > 0) {
    jsonLd.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.question,
        acceptedAnswer: { "@type": "Answer", text: f.answer },
      })),
    });
  }

  const safeHtml = DOMPurify.sanitize(post.content_html ?? "");

  return (
    <>
      <Seo
        title={`${post.title} | Quickatoo`}
        description={post.meta_description ?? post.title}
        path={`/blog/${post.slug}`}
        type="article"
        jsonLd={jsonLd}
      />
      <Navbar />
      <main className="min-h-screen bg-background pt-32 pb-20">
        <article className="container mx-auto max-w-3xl px-4">
          <Link to="/blog" className="text-sm font-medium text-primary">
            ← Back to the blog
          </Link>
          <h1 className="mt-6 text-4xl font-bold leading-tight text-foreground md:text-5xl">{post.title}</h1>
          <time className="mt-4 block text-sm text-muted-foreground" dateTime={post.published_at}>
            {formatDate(post.published_at)}
          </time>

          {post.hero_image_url && (
            <img
              src={post.hero_image_url}
              alt={post.hero_image_alt ?? post.title}
              className="mt-8 w-full rounded-2xl object-cover"
            />
          )}

          <div
            className="prose prose-lg mt-10 max-w-none text-foreground prose-headings:text-foreground prose-a:text-primary prose-strong:text-foreground"
            dangerouslySetInnerHTML={{ __html: safeHtml }}
          />

          {post.infographic_image_url && (
            <img
              src={post.infographic_image_url}
              alt={`${post.title} infographic`}
              loading="lazy"
              className="mt-10 w-full rounded-2xl"
            />
          )}

          {faqs.length > 0 && (
            <section className="mt-14">
              <h2 className="text-2xl font-bold text-foreground">Frequently asked questions</h2>
              <dl className="mt-6 space-y-6">
                {faqs.map((faq, i) => (
                  <div key={i} className="rounded-xl border border-border bg-card p-6">
                    <dt className="font-semibold text-foreground">{faq.question}</dt>
                    <dd className="mt-2 text-muted-foreground">{faq.answer}</dd>
                  </div>
                ))}
              </dl>
            </section>
          )}
        </article>
      </main>
      <Footer {...footerData} />
    </>
  );
};

export default BlogPost;
